<template>
  <div class="container-fluid sale-detail">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando detalle de presupuesto...</p>
    </div>

    <div v-else class="sale-detail-content">
      <!-- View header -->
      <div class="view-header my-4">
        <nav class="view-header__breadcrumb" aria-label="breadcrumb">
          <router-link :to="{ name: 'Presupuestos' }">Presupuestos</router-link>
          <i class="bi bi-chevron-right view-header__breadcrumb-sep"></i>
          <template v-if="budget.pos && budget.number">
            <span class="view-header__breadcrumb-current">
              {{ padLeft(budget.pos, 4) }}-{{ padLeft(budget.number, 8) }}
            </span>
          </template>
          <span v-else class="view-header__breadcrumb-current">#{{ budgetId }}</span>
        </nav>

        <div class="view-header__main">
          <div class="view-header__info">
            <h1 class="view-header__title">Detalle de presupuesto</h1>
            <p class="view-header__subtitle">Información detallada del presupuesto.</p>
          </div>

          <div class="view-header__actions">
            <DownloadDoc
              :pdfurl="pdfurl"
              :open="true"
              :filename="budget.client.name + budget.created_at + '.pdf'"
            >
              <button type="button" class="btn view-header__btn">
                <i class="bi bi-printer"></i>
                Imprimir
              </button>
            </DownloadDoc>
            <DownloadDoc
              :pdfurl="pdfurl"
              :open="false"
              :filename="budget.client.name + budget.created_at + '.pdf'"
            >
              <button type="button" class="btn view-header__btn view-header__btn--primary">
                <i class="bi bi-download"></i>
                Descargar PDF
              </button>
            </DownloadDoc>
          </div>
        </div>
      </div>

      <!-- Invoice header card -->
      <div class="card invoice-header-card mb-4">
        <div class="invoice-header-main">
          <div class="invoice-type-block">
            <div class="invoice-type-icon">
              <i class="bi bi-file-earmark-text"></i>
            </div>
            <div class="invoice-type-info">
              <span class="invoice-type-label">Tipo de documento</span>
              <span class="invoice-type-value">
                Presupuesto<template v-if="budget.letter"> {{ budget.letter }}</template>
              </span>
              <span v-if="budget.letter && budget.letter !== 'P'" class="invoice-type-badge">
                Original
              </span>
            </div>
          </div>

          <div class="invoice-client-block">
            <div class="invoice-client-title">
              <span class="client-title-icon">
                <i class="bi bi-person"></i>
              </span>
              <span>Datos del cliente</span>
            </div>

            <div class="invoice-client-grid">
              <div class="client-field">
                <span class="field-label">Nombre / Razón social</span>
                <span class="field-value">{{ budget.client.name || '—' }}</span>
              </div>

              <div class="client-field">
                <span class="field-label">
                  <i class="bi bi-calendar3 field-icon"></i>
                  Fecha de emisión
                </span>
                <span class="field-value">{{ createdDateFormatted || '—' }}</span>
              </div>

              <div v-if="budget.number && budget.pos" class="client-field">
                <span class="field-label">
                  <i class="bi bi-file-text field-icon"></i>
                  N° de presupuesto
                </span>
                <span class="field-value">
                  {{ padLeft(budget.pos, 4) }}-{{ padLeft(budget.number, 8) }}
                </span>
              </div>

              <div v-if="budget.client_cuit" class="client-field">
                <span class="field-label">
                  <i class="bi bi-card-text field-icon"></i>
                  CUIT
                </span>
                <span class="field-value">{{ budget.client_cuit }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasSecondaryInfo" class="invoice-header-secondary">
          <div v-if="clerkname" class="secondary-item">
            <span class="secondary-label">Vendedor</span>
            <span class="secondary-value">{{ clerkname }}</span>
          </div>
          <div v-if="budget.response && budget.response['CAE']" class="secondary-item">
            <span class="secondary-label">CAE</span>
            <span class="secondary-value secondary-value--mono">{{ budget.response['CAE'] }}</span>
            <span v-if="caeExpDateFormatted" class="secondary-sublabel">
              (vto. {{ caeExpDateFormatted }})
            </span>
          </div>
        </div>
      </div>

      <!-- Items + totals -->
      <div class="row">
        <!-- Items table -->
        <div class="col-md-8 mb-4 mb-md-0">
          <div class="card items-card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-box-seam"></i>
                </span>
                <h5 class="mb-0">Items</h5>
              </div>
              <div v-if="budget.budget_details && budget.budget_details.length > 0" class="items-summary">
                <span class="items-summary-item">
                  Total de ítems: <strong>{{ budget.budget_details.length }}</strong>
                </span>
                <span v-if="totalUnits > 0" class="items-summary-sep">|</span>
                <span v-if="totalUnits > 0" class="items-summary-item">
                  Total de unidades: <strong>{{ totalUnits }}</strong>
                </span>
              </div>
            </div>
            <div class="card-body p-4">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th class="col-small">Cod</th>
                      <th>Item</th>
                      <th class="col-small text-center">Cantidad</th>
                      <th class="col-small text-right">Precio unit.</th>
                      <th class="col-small text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(value, idx) in budget.budget_details"
                      :key="idx"
                    >
                      <td class="text-muted">{{ value.code }}</td>
                      <td>{{ value.description }}</td>
                      <td class="text-center">{{ value.quantity }}</td>
                      <td class="text-right">{{ formatoDinamicoMoneda(value.price) }}</td>
                      <td class="text-right font-weight-bold">
                        {{ formatoDinamicoMoneda(value.price * value.quantity) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Totals panel -->
        <div class="col-md-4">
          <div class="card totals-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-coin"></i>
                </span>
                <h5 class="mb-0">Totales del presupuesto</h5>
              </div>
            </div>
            <div class="card-body">
              <div class="total-row">
                <span class="label">Subtotal</span>
                <span class="value">{{ formatoDinamicoMoneda(budget.subtotal) }}</span>
              </div>

              <div v-if="budget.discount > 0" class="total-row">
                <span class="label">Descuento</span>
                <span class="value text-success">
                  − {{ formatoDinamicoMoneda(budget.discount) }}
                </span>
              </div>

              <template v-if="budget.letter === 'A' || budget.letter === 'M'">
                <div
                  v-for="(tax, key) in budget.taxes"
                  :key="key"
                  class="total-row"
                >
                  <span class="label">IVA {{ key }}%</span>
                  <span class="value">{{ formatoDinamicoMoneda(tax) }}</span>
                </div>
              </template>

              <div class="totals-divider"></div>

              <div class="total-row total-final">
                <span class="label">Total</span>
                <span class="value">{{ formatoDinamicoMoneda(budget.total) }}</span>
              </div>
            </div>
          </div>

          <div v-if="budget.notes" class="card notes-card">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-sticky"></i>
                </span>
                <h6 class="mb-0">Nota para el cliente</h6>
              </div>
            </div>
            <div class="card-body">
              <p class="mb-0 note-text">{{ budget.notes }}</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import DownloadDoc from "../../global/DownloadDoc";
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";

export default {
  components: {
    DownloadDoc,
  },
  mixins: [formatMoneyMixin],
  props: ["budgetId"],
  data() {
    return {
      loading: true,
      pdfurl: process.env.VUE_APP_BASE_URL + '/api/budgets/budPDF/' + this.budgetId,
      budget: {
        letter: null,
        pos: null,
        number: null,
        client_id: null,
        subtotal: null,
        discount: null,
        taxes: null,
        total: null,
        notes: '',
        budget_details: [],
        client: {
          name: null,
          clerk: { name: null },
        },
      },
    };
  },
  computed: {
    createdDateFormatted() {
      if (!this.budget.created_at) return '';
      return new Date(this.budget.created_at).toLocaleDateString('es-AR');
    },
    clerkname() {
      return this.budget.client.clerk ? this.budget.client.clerk.name : '';
    },
    caeExpDateFormatted() {
      const raw = this.budget.response && this.budget.response['CAEFchVto'];
      if (!raw) return '';
      const str = String(raw);
      if (/^\d{8}$/.test(str)) {
        return `${str.slice(6, 8)}/${str.slice(4, 6)}/${str.slice(0, 4)}`;
      }
      const d = new Date(str);
      return isNaN(d) ? str : d.toLocaleDateString('es-AR');
    },
    hasSecondaryInfo() {
      return (
        !!this.clerkname ||
        (this.budget.response && this.budget.response['CAE'])
      );
    },
    totalUnits() {
      if (!Array.isArray(this.budget.budget_details)) return 0;
      return this.budget.budget_details.reduce((sum, item) => {
        const q = Number(item.quantity) || 0;
        return sum + q;
      }, 0);
    },
  },
  mounted() {
    this.getBudgetDetail(this.budgetId);
  },
  methods: {
    getBudgetDetail(budgetId) {
      this.loading = true;
      this.$http
        .get('/api/budgets/getBudget/' + budgetId)
        .then(response => {
          if (response.status === 200) {
            this.budget = response.data.data;
          }
        })
        .catch(error => {
          console.error('Error al cargar detalle de presupuesto:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    padLeft(number, length) {
      return number.toString().padStart(length, '0');
    },
  },
};
</script>
